import {
  addStageToCompleted, calculateProgress, isParallelStagesCompleted, removeStageFromActive,
} from 'src/util/loading/parallelStageHelpers';

import {
  LoadingMessageKeys,
  STAGE_PROGRESS,
  StageKeys,
  TradeListLoadingActions,
  TradeListLoadingStates,
} from './constants';

/**
 * The two data stages that run in parallel after meters are loaded.
 * Used by isParallelStagesCompleted to check the gate condition.
 */
const PARALLEL_STAGES = [StageKeys.RULES, StageKeys.TRADES];

/**
 * Wrapper for isParallelStagesCompleted that binds TradeList's PARALLEL_STAGES.
 * @param {string[]} completedStages - Array of completed stage keys.
 * @returns {boolean} True if all parallel stages (RULES and TRADES) are completed.
 */
const checkParallelStagesCompleted = (completedStages) => (
  isParallelStagesCompleted(completedStages, PARALLEL_STAGES)
);

export const defaultInitialState = () => ({
  state: TradeListLoadingStates.IDLE,
  activeStages: [],
  completedStages: [],
  metadata: {
    metersCount: 0,
    rulesCount: 0,
    tradesCount: 0,
    error: null,
  },
  overlay: {
    progressBarValue: 0,
    messageKey: LoadingMessageKeys.NONE,
  },
});

/**
 * Validates and normalizes a count value to ensure it's a non-negative number.
 * @param {*} value - Value to validate
 * @returns {number} Validated count (0 if invalid)
 */
export const validateCount = (value) => (typeof value === 'number' && value >= 0 ? value : 0);

/**
 * State machine reducer for the TradeList loading pipeline.
 *
 * This reducer manages the state transitions and context updates for the
 * multi-stage trade data fetching process. It is intentionally side-effect
 * free to simplify testing and reasoning about state changes.
 *
 * State Flow (Parallel Loading):
 *
 * 1.  IDLE → INITIATE → LOADING_METERS
 * 2.  LOADING_METERS → METERS_LOADED(complete: false) → LOADING_METERS
 * 3.  LOADING_METERS → METERS_LOADED(complete: true) → LOADING_RULES
 *     (At this point, RULES and TRADES stages start in parallel)
 * 4.  LOADING_RULES → RULES_LOADED(complete: false) → LOADING_RULES
 * 5.  LOADING_RULES → TRADES_LOADED(complete: false) → LOADING_RULES
 * 6.  LOADING_RULES → RULES_LOADED(complete: true) → LOADING_RULES (if TRADES pending)
 *                                                  → PREPARING_DATA (if TRADES done)
 * 7.  LOADING_RULES → TRADES_LOADED(complete: true) → LOADING_RULES (if RULES pending)
 *                                                   → PREPARING_DATA (if RULES done)
 * 8.  PREPARING_DATA → DATA_PREPARED → RENDERING_DATA
 * 9.  RENDERING_DATA → DATA_RENDERED → COMPLETE
 * 10. COMPLETE → HIDE_OVERLAY → IDLE
 * 11. Any state → RESET → IDLE
 *
 * Note: LOADING_RULES serves as the "parallel loading" state where both RULES and
 * TRADES stages execute concurrently. The activeStages array tracks in-progress
 * stages, while completedStages tracks finished stages. Transition to PREPARING_DATA
 * only occurs when isParallelStagesCompleted() returns true (gate logic).
 *
 * Note: CANCELLED and ERROR are terminal states. Only actions listed in
 * allowedInTerminalState (RESET, INITIATE, CANCEL_REQUEST, HIDE_OVERLAY) can
 * transition out of these states. LOADING_COMPLETE is intentionally excluded so
 * a cache hit after a cancel or error does not re-trigger the loading overlay —
 * the INITIATE action (dispatched at the start of any real network fetch) is the
 * correct way to leave a terminal state.
 * @param {object} state - Current state of the machine.
 * @param {object} action - Action to be performed, with associated metadata.
 * @param {string} action.type - Type of action to perform (see TradeListLoadingActions).
 * @param {number} [action.addedMetersCount] - Meter count to add to the internal
 *                                             loaded meters count.
 * @param {number} [action.addedRulesCount] - Rules count to add to the internal
 *                                            loaded rules count.
 * @param {number} [action.addedTradesCount] - Trades count to add to the internal
 *                                             loaded trades count.
 * @param {boolean} [action.complete] - Indicates if the data loaded is the final
 *                                             load for that action.
 * @param {Error} [action.error] - Error for RAISE_ERROR
 * @returns {object} New state after applying the action
 */
export const loadingStateReducer = (state, action) => {
  const TERMINAL_STATES = [TradeListLoadingStates.CANCELLED, TradeListLoadingStates.ERROR];
  const allowedInTerminalState = [
    TradeListLoadingActions.RESET,
    TradeListLoadingActions.INITIATE,
    TradeListLoadingActions.CANCEL_REQUEST,
    TradeListLoadingActions.HIDE_OVERLAY,
  ];

  if (TERMINAL_STATES.includes(state.state) && !allowedInTerminalState.includes(action.type)) {
    return state;
  }

  switch (action.type) {
    case TradeListLoadingActions.RESET:
      return {
        ...defaultInitialState(),
      };

    case TradeListLoadingActions.INITIATE:
      // Property is already known/loaded when TradeList is rendered.
      // We keep PROPERTY as a completed stage for checklist consistency.
      return {
        state: TradeListLoadingStates.LOADING_METERS,
        activeStages: [StageKeys.METERS],
        completedStages: [StageKeys.PROPERTY],
        metadata: {
          ...defaultInitialState().metadata,
        },
        overlay: {
          progressBarValue: calculateProgress([StageKeys.PROPERTY], STAGE_PROGRESS),
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };

    case TradeListLoadingActions.METERS_LOADED: {
      const addedCount = validateCount(action.addedMetersCount);

      // Always ensure PROPERTY is counted as completed for UI consistency.
      const completedStagesBase = addStageToCompleted(
        state.completedStages || [],
        StageKeys.PROPERTY,
      );
      const metersCount = (state.metadata?.metersCount || 0) + addedCount;

      if (!action.complete) {
        const completedStages = completedStagesBase;
        return {
          state: TradeListLoadingStates.LOADING_METERS,
          activeStages: [StageKeys.METERS],
          completedStages,
          metadata: {
            ...state.metadata,
            metersCount,
          },
          overlay: {
            progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
            messageKey: LoadingMessageKeys.METERS_LOADED,
          },
        };
      }

      // Meters fully loaded: start RULES and TRADES in parallel.
      const completedStages = addStageToCompleted(completedStagesBase, StageKeys.METERS);

      return {
        state: TradeListLoadingStates.LOADING_RULES,
        activeStages: [StageKeys.RULES, StageKeys.TRADES],
        completedStages,
        metadata: {
          ...state.metadata,
          metersCount,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };
    }

    case TradeListLoadingActions.RULES_LOADED: {
      const addedCount = validateCount(action.addedRulesCount);
      const rulesCount = (state.metadata?.rulesCount || 0) + addedCount;

      // Incremental progress updates while stage is active.
      if (!action.complete) {
        return {
          ...state,
          state: TradeListLoadingStates.LOADING_RULES,
          metadata: {
            ...state.metadata,
            rulesCount,
          },
        };
      }

      const activeStages = removeStageFromActive(state.activeStages, StageKeys.RULES);
      const completedStages = addStageToCompleted(state.completedStages || [], StageKeys.RULES);
      const allDataLoaded = checkParallelStagesCompleted(completedStages);

      return {
        state: allDataLoaded
          ? TradeListLoadingStates.PREPARING_DATA
          : TradeListLoadingStates.LOADING_RULES,
        activeStages: allDataLoaded
          ? [StageKeys.DATA_PREPARED]
          : activeStages,
        completedStages,
        metadata: {
          ...state.metadata,
          rulesCount,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.PREPARING_DATA
            : LoadingMessageKeys.RULES_LOADED,
        },
      };
    }

    case TradeListLoadingActions.TRADES_LOADED: {
      const addedCount = validateCount(action.addedTradesCount);
      const tradesCount = (state.metadata?.tradesCount || 0) + addedCount;

      // Incremental progress updates while stage is active.
      // Note: We stay in LOADING_RULES because it serves as the "parallel loading"
      // state for both RULES and TRADES stages. The activeStages array tracks which
      // specific stages are running, while the high-level state remains LOADING_RULES
      // until the gate condition (isParallelStagesCompleted) is satisfied.
      if (!action.complete) {
        return {
          ...state,
          state: TradeListLoadingStates.LOADING_RULES,
          metadata: {
            ...state.metadata,
            tradesCount,
          },
        };
      }

      const activeStages = removeStageFromActive(state.activeStages, StageKeys.TRADES);
      const completedStages = addStageToCompleted(state.completedStages || [], StageKeys.TRADES);
      const allDataLoaded = checkParallelStagesCompleted(completedStages);

      return {
        state: allDataLoaded
          ? TradeListLoadingStates.PREPARING_DATA
          : TradeListLoadingStates.LOADING_RULES,
        activeStages: allDataLoaded
          ? [StageKeys.DATA_PREPARED]
          : activeStages,
        completedStages,
        metadata: {
          ...state.metadata,
          tradesCount,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.PREPARING_DATA
            : LoadingMessageKeys.TRADES_LOADED,
        },
      };
    }

    case TradeListLoadingActions.LOADING_COMPLETE: {
      const completedStages = [
        StageKeys.PROPERTY,
        StageKeys.METERS,
        StageKeys.RULES,
        StageKeys.TRADES,
      ];

      return {
        state: TradeListLoadingStates.PREPARING_DATA,
        activeStages: [StageKeys.DATA_PREPARED],
        completedStages,
        metadata: {
          ...state.metadata,
          metersCount: action.metersCount ?? state.metadata.metersCount,
          rulesCount: action.rulesCount ?? state.metadata.rulesCount,
          tradesCount: action.tradesCount ?? state.metadata.tradesCount,
          error: null,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
          messageKey: LoadingMessageKeys.PREPARING_DATA,
        },
      };
    }

    case TradeListLoadingActions.DATA_PREPARED: {
      const activeStages = [StageKeys.DATA_RENDERED];
      const completedStages = addStageToCompleted(
        state.completedStages || [],
        StageKeys.DATA_PREPARED,
      );
      return {
        state: TradeListLoadingStates.RENDERING_DATA,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
          messageKey: LoadingMessageKeys.RENDERING,
        },
      };
    }

    case TradeListLoadingActions.DATA_RENDERED: {
      const activeStages = [];
      const completedStages = addStageToCompleted(
        state.completedStages || [],
        StageKeys.DATA_RENDERED,
      );
      return {
        state: TradeListLoadingStates.COMPLETE,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
          messageKey: LoadingMessageKeys.COMPLETE,
        },
      };
    }

    case TradeListLoadingActions.HIDE_OVERLAY: {
      return {
        ...defaultInitialState(),
      };
    }

    case TradeListLoadingActions.RAISE_ERROR: {
      return {
        state: TradeListLoadingStates.ERROR,
        metadata: {
          ...state.metadata,
          error: action.error || null,
        },
        activeStages: [],
        completedStages: [...(state.completedStages || [])],
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };
    }

    case TradeListLoadingActions.CANCEL_REQUEST:
      return {
        state: TradeListLoadingStates.CANCELLED,
        metadata: {
          ...state.metadata,
          error: null,
        },
        activeStages: [],
        completedStages: [...(state.completedStages || [])],
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    default:
      return state;
  }
};
